import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'pymed.theme';
const MODES = ['light', 'dark'];

const ColorModeContext = createContext(null);

/** The OS preference, used only until the user picks a side themselves. */
const systemMode = () => {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  } catch {
    // matchMedia is missing under jsdom and in a few older browsers.
    return 'light';
  }
};

const readStored = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(stored) ? stored : null;
  } catch {
    // Private windows can throw on access rather than return null.
    return null;
  }
};

/**
 * Light/dark mode for the whole app.
 *
 * The mode is written to `<html data-theme>`, which is what the Ocean CSS
 * variables key off (see MODE_VARS in theme/oceanTokens.js). That means the
 * rail, panels, rules and table chrome all repaint from the attribute alone,
 * and the MUI palette swap handles the components on top.
 *
 * First visit follows the OS; after that an explicit choice wins for good,
 * because someone who picked light does not want the OS overriding them at
 * sunset.
 */
export const ColorModeProvider = ({ children, forced }) => {
  const [stateMode, setMode] = useState(() => readStored() || systemMode());
  // `forced` pins the app to one mode while the toggle is hidden from the UI.
  // Without it, removing the control would strand anyone whose OS is dark, or
  // who had already chosen dark, in a dark app with no way back.
  const mode = MODES.includes(forced) ? forced : stateMode;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = mode;
    // Tells the browser which way to paint form controls and scrollbars.
    root.style.colorScheme = mode;
    // A forced mode is not the user's choice, so it is not recorded: whatever
    // they picked before is still there when the toggle comes back.
    if (forced) return;
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* Nothing to do: the mode still applies for this session. */
    }
  }, [mode, forced]);

  const toggle = useCallback(
    () => setMode((m) => (m === 'dark' ? 'light' : 'dark')),
    []
  );

  const choose = useCallback((next) => {
    if (MODES.includes(next)) setMode(next);
  }, []);

  const value = useMemo(
    () => ({ mode, isDark: mode === 'dark', toggle, choose }),
    [mode, toggle, choose]
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export const useColorMode = () => {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error('useColorMode must be used within a ColorModeProvider');
  return ctx;
};
